import { QueryFactory } from '@lmt/shared-types';
import { AuditState, AuditRecordDto } from '@lmt/shared-types';
import { AuditFilter } from '@lmt/unicorn-shared/app/components/audit/AuditFilter';
import {
  convertAuditRecord,
  emptyString,
} from '@lmt/unicorn-shared/app/components/audit/operations';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { TablePagination } from '@lmt/unicorn-shared/app/components/common/table/TablePagination';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import { AppTable } from '@lmt/unicorn-shared/app/components/common/ui-kit/table/AppTable';
import { useUpdateSortByInUrl } from '@lmt/unicorn-shared/app/components/common/ui-kit/table/useUpdateSortByInUrl';
import {
  DateTimeParamUnicorn,
  localFormat,
} from '@lmt/unicorn-shared/app/helpers/date-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import {
  createNumericArrayParam,
  useQueryParamsInTab,
} from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { useEventCallback } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { Column, useSortBy, useTable } from 'react-table';
import { NumberParam, StringParam } from 'react-router-url-params';
import {
  pagingQueryParams,
  sortingQueryParams,
  toQueryParams,
} from '../common/table/operations';
import styles from './Audit.module.scss';
import { useTabs } from '../../features/navigation/operations';
import { pages } from '../../constants/sharedRoutes';
import { TagsViewer } from '../common/ui-kit/tags/TagsViewer';

export const AuditPage: FC = React.memo(function AuditPage() {
  const i18next = useScopedTranslation('audit');
  const [queryParams, setQueryParams] = useQueryParamsInTab({
    table: StringParam,
    subtenantIds: createNumericArrayParam(),
    user: StringParam,
    startDate: DateTimeParamUnicorn,
    endDate: DateTimeParamUnicorn,
    patientId: NumberParam,
    rowKey: StringParam,
    ...pagingQueryParams(25),
    ...sortingQueryParams(nameof<AuditRecordDto>('dateTime'), true),
  });
  const tabs = useTabs();

  const [searchKey, setSearchKey] = useState(1);
  const updateFilters = useEventCallback(
    (params: Parameters<typeof setQueryParams>[0]) => {
      setQueryParams({ ...params, page: 0 });
    },
  );

  const openPatientTab = useEventCallback((patientId: number) => {
    tabs.openTab(pages.patientProfile.link({ patientId: patientId }));
  });

  const onResetFilters = useEventCallback(() => {
    updateFilters({
      table: null,
      patientId: null,
      subtenantIds: null,
      user: null,
      rowKey: null,
      startDate: null,
      endDate: null,
    });
    setSearchKey((patientSearchKey) => ++patientSearchKey);
  });

  const resultQuery = QueryFactory.AuditQuery.useGetAuditRecordsQuery(
    {
      tableName: queryParams.table,
      patientId: queryParams.patientId,
      subtenantIds: queryParams.subtenantIds,
      userId: queryParams.user,
      rowKey: queryParams.table ? queryParams.rowKey : null,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
      filterCondition: {},
      ...toQueryParams(queryParams),
    },
    {
      select: (result) => {
        const convertedData = (result.data ?? []).map((x) =>
          convertAuditRecord(x),
        );
        return {
          data: convertedData,
          totalCount: result.totalCount,
        };
      },
    },
  );
  const table = useTable<AuditRecordDto>(
    {
      data: resultQuery.data?.data ?? [],
      columns: useMemo(() => {
        return [
          {
            id: 'dateTime',
            accessor: (row) => localFormat(row.dateTime, 'Ppp'),
            Header: i18next.t('header.time'),
            width: '15%',
            disableSortBy: false,
          },
          {
            id: 'tableName',
            accessor: 'tableName',
            Header: i18next.t('header.table_name'),
            width: '10%',
            disableSortBy: true,
          },
          {
            id: 'patientName',
            accessor: 'patientName',
            Header: i18next.t('header.patient_name'),
            width: '10%',
            disableSortBy: true,
            Cell: (props) => (
              <div
                className={styles.patient}
                onClick={(e) => {
                  e.stopPropagation();
                  const patientId = props.row.original.patientId;
                  if (patientId) {
                    openPatientTab(patientId);
                  }
                }}
              >
                {props.value}
              </div>
            ),
          },
          {
            id: 'rowKey',
            accessor: 'rowKey',
            Header: i18next.t('header.key'),
            width: '10%',
            disableSortBy: true,
          },
          {
            id: 'changes',
            accessor: (row) => {
              const values = (row as any).changes;
              return Object.keys(values).map((property) => {
                const propertyValue = values[property];
                return (
                  propertyValue !== emptyString && (
                    <div className={styles.changes} key={property}>
                      <TagsViewer
                        text={`${property ? property + ': ' : ''}${
                          (typeof propertyValue === 'object' ||
                            typeof propertyValue === 'boolean') &&
                          propertyValue !== null
                            ? JSON.stringify(propertyValue)
                            : propertyValue
                        }`}
                      />
                    </div>
                  )
                );
              });
            },
            Header: i18next.t('header.changes'),
            width: '*',
            disableSortBy: true,
          },
          {
            id: 'state',
            accessor: (row) => AuditState[row.state],
            Header: i18next.t('header.state'),
            width: '10%',
            disableSortBy: true,
          },
          {
            id: 'userName',
            accessor: 'userName',
            Header: i18next.t('header.user'),
            width: '10%',
            disableSortBy: true,
          },
        ] as Column<AuditRecordDto>[];
      }, []),
      manualSortBy: true,
    },
    useSortBy,
  );
  useUpdateSortByInUrl(table.state.sortBy);
  return (
    <div className={styles.pageContainer} data-test-id="table-page-container">
      <div className={styles.tableToolbar}>
        <TableTitle
          title={i18next.t('table_title')}
          count={resultQuery.data?.totalCount}
        />
      </div>
      {resultQuery.isError ? (
        <div>{convertToErrorString(resultQuery.error)}</div>
      ) : (
        <>
          <div className={styles.patientsTable}>
            <Loading
              loading={resultQuery.isLoading}
              title={i18next.t('loading')}
            >
              {resultQuery.data?.totalCount === 0 ? (
                <div className={styles.emptyList}>
                  <div className={styles.emptyListText}>
                    {i18next.t('empty_list_text')}
                  </div>
                </div>
              ) : (
                <AppTable table={table} />
              )}
            </Loading>
          </div>
          <TablePagination
            pagination={queryParams}
            pageLimitTitle={i18next.t('pagination_page_limit')}
            changePagination={setQueryParams}
            totalCount={resultQuery.data?.totalCount}
          />
        </>
      )}

      <div className={styles.filtersBlockContainer}>
        <AuditFilter
          search={''}
          searchKey={searchKey}
          from={queryParams.startDate ?? null}
          to={queryParams.endDate ?? null}
          patientId={queryParams.patientId ?? null}
          user={queryParams.user ?? null}
          table={queryParams.table ?? null}
          rowKey={queryParams.rowKey ?? null}
          subtenantIds={queryParams.subtenantIds}
          onFromChanged={(value) => {
            updateFilters({ startDate: value ?? null });
          }}
          onToChanged={(value) => {
            updateFilters({ endDate: value ?? null });
          }}
          onUserChanged={(value) => {
            updateFilters({ user: value ?? null });
          }}
          onTableChanged={(value) => {
            updateFilters({
              table: value ?? null,
              rowKey: null,
            });
            setSearchKey((current) => current + 1);
          }}
          onSubtenantsChanged={(value) => {
            updateFilters({ subtenantIds: value });
          }}
          onPatientChanged={(value) => {
            updateFilters({ patientId: value?.id });
          }}
          onKeyChanged={(value) => {
            const key = value?.trim();

            updateFilters({
              rowKey: key ? key : null,
            });
          }}
          onReset={onResetFilters}
        />
      </div>
    </div>
  );
});
